import { BaseComponent, Building } from '../types/protocols';
import { BaseComponentImpl } from '../types/implementations';
import { BaseType } from '../types/Enums';
import { resolveComponentLocation } from './ComponentRoomUtils';
import { listGroupForComponent } from './componentDrawing';
import { MOUNTING_SURFACE_LABELS } from './roofPlan';

/**
 * Where a linked component is, as the words a card shows under its name.
 *
 * `level` and `room` come from `resolveComponentLocation` (own room, parent
 * rooms, then the parent surface's room). When no room resolves, `place`
 * says what the component sits on instead, so a facade window or a
 * roof-mounted fan is not shown without any location at all:
 *   - a utility or Övrigt mounted on a roof or facade → "Tak" / "Fasad"
 *   - anything else with a parent surface → that surface's name ("Fasad norr")
 *   - else the list group it belongs to: "Takplan" for a roof, "Utomhus" for
 *     what lives on the satellite image (facade, outdoor, an outdoor utility)
 *   - null for the rest (a foundation, a wall in a room that no longer exists)
 */
export interface ComponentLocationLabel {
    level: string | null;
    room: string | null;
    place: string | null;
}

/** The first parent surface that still exists (a takfönster may have lost its ceiling but not its roof). */
const parentSurface = (component: BaseComponent, components: { [key: string]: BaseComponent } | undefined): BaseComponent | undefined => {
    for (const key of [component.parentSurface?.value, component.parentSurface1?.value, component.parentSurface2?.value]) {
        const parent = key ? components?.[key] : undefined;
        if (parent) return parent;
    }
    return undefined;
};

export const componentLocationLabel = (component: BaseComponent, building: Building): ComponentLocationLabel => {
    const structure = building.model?.structure;
    const location = resolveComponentLocation(component, structure);
    if (location.roomDisplay || location.levelDisplay) {
        return { level: location.levelDisplay, room: location.roomDisplay, place: null };
    }

    const parent = parentSurface(component, structure?.components);
    if (parent) {
        const isMountable = component.type === BaseType.Utility || component.type === BaseType.Miscellaneous;
        if (isMountable && parent.type === BaseType.Roof) return { level: null, room: null, place: MOUNTING_SURFACE_LABELS.roof };
        if (isMountable && parent.type === BaseType.Facade) return { level: null, room: null, place: MOUNTING_SURFACE_LABELS.facade };
        return { level: null, room: null, place: new BaseComponentImpl(parent).displayName(building) };
    }

    return { level: null, room: null, place: listGroupForComponent(component, structure)?.label ?? null };
};

/**
 * The label as one line for a list row: "Våning 01 • Rum 12 - Kök", "Rum 12"
 * or "Plats: Fasad (Nord)"; null when nothing resolves.
 */
export const componentLocationSubtitle = ({ level, room, place }: ComponentLocationLabel): string | null => {
    if (level || room) return [level, room].filter(Boolean).join(' • ');
    return place ? `Plats: ${place}` : null;
};
